import React, { useMemo } from 'react'
import { BellOutlined, PictureOutlined } from '@ant-design/icons'
import { Segmented, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { AntdTableEmptyPlaceholder } from '@pushly/aqe/lib/components'
import {
    DASHBOARD_TABLE_ROW_CLASS_NAME,
    DASHBOARD_TABLE_SIZE,
    dashboardTableClassName,
} from '../../components/dashboard-table-parts'
import { ClampedLine } from '../../components/clamped-line'
import { SectionError } from '../../components/section-error'
import { NOTIFICATION_THUMBNAIL_SIZE } from '../../components/notification-thumbnail-size'
import { SourceBadge } from '@pushly/aqe/lib/components/badges'
import { NOTIFICATIONS_TABLE_ROW_HEIGHT, tableRowHeightStyle } from '../../components/table-row-heights'
import { TableSkeleton } from '../../components/table-skeleton'
import { TopNotificationReportRow } from '../../data/contracts'
import { NotificationRankingChoice, RankingTable } from '../../data/use-org-dashboard-rankings'
import { NO_VALUE, formatCountInFull, formatMoneyInFull, formatRate } from '../../math/format'
import { DateWindow } from '../../math/windows'
import { DashboardTemplate, primaryRankingMeasureFor } from '../../template'
import { DashboardTableCard } from '../../components/dashboard-table-card'
import { NotificationRow, notificationRowFrom, rankingOptionsFor } from './content-model'
import { rankingColumnProps } from './ranking-column'

const NOTIFICATIONS_TABLE_CLASS_NAME = 'org-dashboard-top-notifications-table'
export const TOP_NOTIFICATIONS_TITLE = 'Top Notifications'
/*
 * Columns whose content has a known shape (a rank, a tag, a figure) get a fixed width sized to the longest value
 * they hold; the notification, whose title and body are free text, takes everything that is left. The table
 * scrolls sideways below the width at which that remainder would be too narrow to read.
 */
/** Ten notifications are ranked, so the placeholder holds ten rows until a table has been seen. */
export const NOTIFICATION_PLACEHOLDER_ROW_COUNT = 10
/** The rank never exceeds ten, so its column holds two digits and no more. */
export const POSITION_COLUMN_WIDTH = 48
export const DOMAIN_COLUMN_WIDTH = 168
export const SOURCE_COLUMN_WIDTH = 96
// "Purch. Rate" is the widest heading in a number column: it sits on one line beside its sort arrow.
export const NUMBER_COLUMN_WIDTH = 124
export const IMPRESSIONS_COLUMN_WIDTH = 128
// Money is written in full with its symbol and cents ("$1,234,567.89"), and the ranked column's figure is bold.
export const REVENUE_COLUMN_WIDTH = 144
// Leaves the notification column about 190px beside the widest set of fixed columns (an e-commerce table).
export const MINIMUM_TABLE_WIDTH = 1144

/** A swap of ranking keeps the rows on screen until the new ones arrive, so the placeholder is as tall as they were. */
export const placeholderRowCountFor = (previousRowCount: number): number =>
    previousRowCount > 0 ? previousRowCount : NOTIFICATION_PLACEHOLDER_ROW_COUNT

type Props = {
    template: DashboardTemplate
    /** Revenue and revenue-ranked figures cannot be compared across currencies, so the ranking falls back to purchases. */
    mixedCurrency: boolean
    table: RankingTable<TopNotificationReportRow>
    range: DateWindow
    ranking: NotificationRankingChoice
    onRankingChange: (ranking: NotificationRankingChoice) => void
    currencyCodeFor: (domainId: number) => string | undefined
    /** A notification of a domain the user cannot switch to is shown as plain text. */
    canOpenDomain: (domainId: number) => boolean
    openInDomain: (domainId: number, path: string, event: React.MouseEvent<HTMLAnchorElement>) => void
    onRetry: () => void
}

/** A measure the response did not carry is a dash, never a zero. */
const countText = (count: number | null): string => (count === null ? NO_VALUE : formatCountInFull(count))

const notificationPath = (row: NotificationRow): string => `/domains/${row.domainId}/notifications/${row.id}/summary`

const Creative: React.FC<{ imageUrl: string | null }> = ({ imageUrl }) =>
    imageUrl ? (
        <img
            className="org-dashboard-notification-image"
            src={imageUrl}
            alt=""
            width={NOTIFICATION_THUMBNAIL_SIZE}
            height={NOTIFICATION_THUMBNAIL_SIZE}
            loading="lazy"
        />
    ) : (
        <div className="org-dashboard-notification-image org-dashboard-notification-image-empty" aria-hidden="true">
            <PictureOutlined />
        </div>
    )

export const TopNotificationsTable: React.FC<Props> = ({
    template,
    mixedCurrency,
    table,
    range,
    ranking,
    onRankingChange,
    currencyCodeFor,
    canOpenDomain,
    openInDomain,
    onRetry,
}) => {
    const options = rankingOptionsFor(template, mixedCurrency)
    const activeOption = ranking === 'primary' ? options[0] : options[1]
    const rows = useMemo(() => table.rows.flatMap((row) => notificationRowFrom(row, range) ?? []), [table.rows, range])
    // Nothing was sent: there is nothing to rank, so there is nothing to choose. A rate ranking that is empty only
    // means no notification reached its minimum, and the primary ranking must stay one click away.
    const nothingWasSent = table.status === 'ready' && rows.length === 0 && ranking === 'primary'
    const rankingColumn = (key: string) => rankingColumnProps(key === activeOption.key)

    const columns: ColumnsType<NotificationRow> = useMemo(() => {
        const ecommerce = template === 'ecommerce'
        const measureColumns: ColumnsType<NotificationRow> = ecommerce
            ? [
                  {
                      key: 'purchase_amount',
                      title: 'Revenue',
                      width: REVENUE_COLUMN_WIDTH,
                      align: 'right',
                      render: (_, row) =>
                          row.purchaseAmount === null
                              ? NO_VALUE
                              : formatMoneyInFull(row.purchaseAmount, currencyCodeFor(row.domainId)),
                      ...rankingColumn('purchase_amount'),
                  },
                  ...(primaryRankingMeasureFor(template, mixedCurrency) === 'purchases'
                      ? [
                            {
                                key: 'purchases',
                                title: 'Purchases',
                                width: NUMBER_COLUMN_WIDTH,
                                align: 'right' as const,
                                render: (_: unknown, row: NotificationRow) => countText(row.purchases),
                                ...rankingColumn('purchases'),
                            },
                        ]
                      : []),
                  {
                      key: 'purchase_rate_decimal',
                      title: 'Purch. Rate',
                      width: NUMBER_COLUMN_WIDTH,
                      align: 'right',
                      render: (_, row) => formatRate(row.purchaseRate, { purchaseRate: true }),
                      ...rankingColumn('purchase_rate_decimal'),
                  },
              ]
            : [
                  {
                      key: 'ctr_decimal',
                      title: 'CTR',
                      width: NUMBER_COLUMN_WIDTH,
                      align: 'right',
                      render: (_, row) => formatRate(row.ctr),
                      ...rankingColumn('ctr_decimal'),
                  },
              ]
        return [
            {
                key: 'position',
                title: '#',
                width: POSITION_COLUMN_WIDTH,
                render: (_, __, index) => index + 1,
            },
            {
                key: 'notification',
                title: 'Notification',
                render: (_, row) => (
                    <div className="org-dashboard-notification-cell">
                        <Creative imageUrl={row.imageUrl} />
                        <div className="org-dashboard-notification-text">
                            <ClampedLine text={row.title} className="org-dashboard-notification-title">
                                {({ ref, className, tabIndex }) =>
                                    canOpenDomain(row.domainId) ? (
                                        <a
                                            ref={ref}
                                            className={className}
                                            href={notificationPath(row)}
                                            onClick={(event) =>
                                                openInDomain(row.domainId, notificationPath(row), event)
                                            }
                                        >
                                            {row.title}
                                        </a>
                                    ) : (
                                        <span ref={ref} className={className} tabIndex={tabIndex}>
                                            {row.title}
                                        </span>
                                    )
                                }
                            </ClampedLine>
                            <ClampedLine text={row.body} className="org-dashboard-notification-body">
                                {({ ref, className, tabIndex }) => (
                                    <div ref={ref} className={className} tabIndex={tabIndex}>
                                        {row.body}
                                    </div>
                                )}
                            </ClampedLine>
                            <ClampedLine text={row.sentLabel} className="org-dashboard-notification-sent">
                                {({ ref, className, tabIndex }) => (
                                    <div ref={ref} className={className} tabIndex={tabIndex}>
                                        {row.sentLabel}
                                    </div>
                                )}
                            </ClampedLine>
                        </div>
                    </div>
                ),
            },
            {
                key: 'domain',
                title: 'Domain',
                width: DOMAIN_COLUMN_WIDTH,
                ellipsis: true,
                render: (_, row) => row.domainName,
            },
            {
                key: 'source',
                title: 'Source',
                width: SOURCE_COLUMN_WIDTH,
                render: (_, row) => <SourceBadge type="notification" source={row.source} variant="filled" expanded />,
            },
            // A Segment column is omitted until the organization-scope notifications contract exposes a selectable segment field.
            {
                key: 'impressions',
                title: 'Impressions',
                width: IMPRESSIONS_COLUMN_WIDTH,
                align: 'right',
                render: (_, row) => countText(row.impressions),
            },
            {
                key: 'clicks',
                title: 'Clicks',
                width: NUMBER_COLUMN_WIDTH,
                align: 'right',
                render: (_, row) => countText(row.clicks),
                ...rankingColumn('clicks'),
            },
            ...measureColumns,
        ]
    }, [template, mixedCurrency, activeOption.key, currencyCodeFor, canOpenDomain, openInDomain])

    const rankingToggle = nothingWasSent ? undefined : (
        <Segmented
            aria-label="Rank notifications by"
            options={options.map((option) => ({ label: option.label, value: option.key }))}
            value={activeOption.key}
            onChange={(key) => onRankingChange(key === options[0].key ? 'primary' : 'secondary')}
        />
    )

    return (
        <DashboardTableCard
            sectionKey="content"
            title={TOP_NOTIFICATIONS_TITLE}
            filters={rankingToggle}
            footnote={
                ranking === 'secondary' && activeOption.floor ? (
                    <span className="org-dashboard-ranking-floor">{activeOption.floor.text}</span>
                ) : undefined
            }
        >
            {table.status === 'error' ? (
                <SectionError sectionName={TOP_NOTIFICATIONS_TITLE} onRetry={onRetry} />
            ) : table.status === 'loading' ? (
                <TableSkeleton
                    columns={columns}
                    shapes={{ position: 'rank', notification: 'media' }}
                    rowCount={placeholderRowCountFor(rows.length)}
                    rowHeight={NOTIFICATIONS_TABLE_ROW_HEIGHT}
                    className={NOTIFICATIONS_TABLE_CLASS_NAME}
                    tableLayout="fixed"
                    scroll={{ x: MINIMUM_TABLE_WIDTH }}
                />
            ) : rows.length === 0 ? (
                <AntdTableEmptyPlaceholder
                    icon={<BellOutlined />}
                    text={nothingWasSent ? 'No notifications sent' : 'No notifications ranked'}
                    description={
                        nothingWasSent
                            ? 'No notifications were sent in this date range.'
                            : 'No notifications reached the minimum for this ranking in this date range.'
                    }
                />
            ) : (
                <Table<NotificationRow>
                    className={dashboardTableClassName(NOTIFICATIONS_TABLE_CLASS_NAME)}
                    size={DASHBOARD_TABLE_SIZE}
                    rowClassName={DASHBOARD_TABLE_ROW_CLASS_NAME}
                    aria-label={TOP_NOTIFICATIONS_TITLE}
                    rowKey={(row) => `${row.domainId}-${row.id}`}
                    columns={columns}
                    dataSource={rows}
                    pagination={false}
                    tableLayout="fixed"
                    scroll={{ x: MINIMUM_TABLE_WIDTH }}
                    style={tableRowHeightStyle(NOTIFICATIONS_TABLE_ROW_HEIGHT)}
                />
            )}
        </DashboardTableCard>
    )
}
